"use strict";
// packages/shared-utils/src/templates/resolveEntityCodeForTemplate.ts
//
// Tenant-scoped entity-code resolver for the case where an upload arrives
// with a known `templateId` but no entity code in its filename (Astute /
// Connective / Choice aggregator filenames are the canonical example).
//
// Why this helper exists
// ----------------------
// Before 2026-05-19 the admin ZIP path resolved the fallback entity code by
// reading the legacy `companies/{c}/entity_mappings` subcollection and
// `.where('templateId', '==', templateId).limit(1)`. That works for legacy
// (Entourage) tenants where `entity_mappings` IS the source of truth, but it
// silently mis-routes uploads for unified-mode tenants:
//
//   - Decru is `loanBookMode: 'unified'`.
//   - Decru's `loan_books` has `peake` (entityCode='Peake', templateId=choice).
//   - Decru's `entity_mappings` historically still contained `LOAN_MARKET__choice`
//     (entityCode='LOAN_MARKET', templateId=choice) from an earlier setup path.
//   - A Peake Choice statement uploaded via the admin ZIP form (filename: no
//     entity code) hit the entity_mappings fallback and was tagged
//     entityCode='LOAN_MARKET'. All 901 commission rows landed under the wrong
//     entity, requiring a manual BQ UPDATE.
//
// The fix: for unified-mode tenants the canonical source of truth is the
// `companies/{c}/loan_books` subcollection — `entity_mappings` may contain
// stale or vestigial entries. For legacy tenants we MUST keep querying
// `entity_mappings` byte-identically (Entourage relies on this with 250k+
// rows; see CLAUDE.md "Do NOT change behavior for legacy-mode tenants").
//
// Failure semantics
// -----------------
// Returns null (i.e. "no resolution; keep raw_commissions.entity_code NULL")
// when:
//
//   - unified-mode + ZERO loan_books match the templateId   (no auto-routing
//     possible; pipeline integrity check pages on null)
//   - unified-mode + MULTIPLE loan_books match (e.g. two enabled entity-typed
//     books share the same templateId — ambiguous, must NOT guess)
//   - legacy-mode + no entity_mappings row matches            (pre-existing
//     behaviour — caller decides what to do with null)
//
// The caller is responsible for the downstream policy (keep null, fail closed,
// alert on integrity check, etc.). This helper never throws and never picks
// a "best guess" — silent mis-routing of $5M+ commission books is exactly the
// failure mode that prompted this helper.
//
// Call sites
// ----------
//   - services/admin-functions/src/uploads/processAdminZipUpload.ts            (admin ZIP)
//   - services/client-functions/src/pipeline/0_ingress/issueSignedUploadUrl.ts (client self-serve,
//     RC-2 fix 2026-06 — final fallback when neither the filename nor the client supply an entity code,
//     so digit-less-filename aggregators like AMAG resolve VISION on the client path too)
//
// (Email-ingestion still routes via filename / pre-resolved registry entries; if it
// ever grows a templateId→entityCode fallback, route it through this helper too.)
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveEntityCodeForTemplate = resolveEntityCodeForTemplate;
exports.resolveCanonicalForFilenameAnchor = resolveCanonicalForFilenameAnchor;
exports.foldFilenameAnchorToCanonical = foldFilenameAnchorToCanonical;
/**
 * Resolve an entity code from a templateId when the upload filename did not
 * carry one. Mode-branched: unified → loan_books, legacy → entity_mappings.
 *
 * @param db          firebase-admin Firestore instance.
 * @param companyId   The tenant the upload is for.
 * @param templateId  The fileTemplate the upload is routed to.
 * @param logger      Optional logger; defaults to console.
 */
async function resolveEntityCodeForTemplate(db, companyId, templateId, logger) {
    const log = logger ?? {
        info: (m, ...meta) => console.log(m, ...meta),
        warn: (m, ...meta) => console.warn(m, ...meta),
    };
    // 1. Resolve loanBookMode.
    const companyDoc = await db.doc(`companies/${companyId}`).get();
    if (!companyDoc.exists) {
        log.warn(`[resolveEntityCodeForTemplate] Company doc missing companyId=${companyId} templateId=${templateId} — returning null`);
        return { entityCode: null, source: 'company_missing', loanBookMode: 'legacy' };
    }
    const rawMode = companyDoc.data()?.loanBookMode;
    const loanBookMode = rawMode === 'unified' ? 'unified' : 'legacy';
    // 2. Mode-branch.
    if (loanBookMode === 'unified') {
        // Source of truth: loan_books with type='entity' that are not explicitly
        // disabled. We filter `isEnabled` IN CODE (treat a missing field as enabled,
        // i.e. `isEnabled !== false`) rather than via `.where('isEnabled','==',true)`:
        // a Firestore equality filter silently EXCLUDES docs that are missing the
        // field, which is a silent-data-loss trap. VFA-stage's loan_books were seeded
        // with `isActive` instead of `isEnabled` (2026-06 incident) and the equality
        // filter made all three invisible → admin-ZIP entity_code landed NULL.
        // `getLoanBooks.ts` already defaults a missing `isEnabled` to true (`?? true`);
        // this mirrors that. Explicitly-disabled docs (`isEnabled:false` — e.g. pending
        // auto-learn entries) are still excluded.
        // Filter by templateId. Exactly one match → resolve. Zero or 2+ → null.
        const snap = await db
            .collection(`companies/${companyId}/loan_books`)
            .where('templateId', '==', templateId)
            .get();
        const candidates = [];
        for (const doc of snap.docs) {
            const data = doc.data();
            if (data.type !== 'entity')
                continue;
            if (data.isEnabled === false)
                continue; // explicitly disabled → exclude; missing field = enabled
            if (!data.entityCode || typeof data.entityCode !== 'string')
                continue;
            candidates.push({ id: doc.id, entityCode: data.entityCode });
        }
        if (candidates.length === 1) {
            const entityCode = candidates[0].entityCode;
            log.info(`[resolveEntityCodeForTemplate] unified-mode resolved entityCode=${entityCode} ` +
                `via loan_books/${candidates[0].id} (companyId=${companyId} templateId=${templateId})`);
            return { entityCode, source: 'loan_books', loanBookMode };
        }
        if (candidates.length === 0) {
            log.warn(`[resolveEntityCodeForTemplate] unified-mode: ZERO enabled entity-typed loan_books ` +
                `matched templateId=${templateId} for companyId=${companyId}. ` +
                `Returning null — raw_commissions.entity_code will be NULL and the integrity ` +
                `check will page. Action: create an enabled entity-typed loan_book for this ` +
                `templateId, or supply the entity code in the filename.`);
            return { entityCode: null, source: 'no_match', loanBookMode };
        }
        // Ambiguous — 2+ enabled entity-typed loan_books share templateId. Refuse
        // to guess. Surface every candidate so the operator can see the conflict.
        const ambiguousCandidates = candidates.map((c) => c.entityCode);
        log.warn(`[resolveEntityCodeForTemplate] unified-mode: AMBIGUOUS — ` +
            `${candidates.length} enabled entity-typed loan_books matched templateId=${templateId} ` +
            `for companyId=${companyId}. Candidates: ${ambiguousCandidates.join(', ')}. ` +
            `Returning null — refusing to guess between siblings. Action: disable all but ` +
            `the canonical loan_book, or supply the entity code in the filename.`);
        return {
            entityCode: null,
            source: 'ambiguous_loan_books',
            loanBookMode,
            ambiguousCandidates,
        };
    }
    // legacy-mode: byte-identical with the historical processAdminZipUpload
    // fallback. Entourage relies on entity_mappings as the source of truth.
    const mappingsSnap = await db
        .collection(`companies/${companyId}/entity_mappings`)
        .where('templateId', '==', templateId)
        .limit(1)
        .get();
    if (!mappingsSnap.empty) {
        const entityCode = mappingsSnap.docs[0].data().entityCode ?? null;
        if (entityCode) {
            log.info(`[resolveEntityCodeForTemplate] legacy-mode resolved entityCode=${entityCode} ` +
                `via entity_mappings/${mappingsSnap.docs[0].id} ` +
                `(companyId=${companyId} templateId=${templateId})`);
        }
        return { entityCode, source: 'entity_mappings', loanBookMode };
    }
    return { entityCode: null, source: 'no_match', loanBookMode };
}
/**
 * Canonical-anchor normalization for unified-mode tenants.
 *
 * **When to call.** The admin-ZIP path extracts a filename anchor via the
 * `^[A-Z][A-Z0-9]{1,9}` regex (`processAdminZipUpload.ts:860`). For tenants
 * that have collapsed multiple anchors onto one loan_book (Decru:
 * DECF100+TRES105 → LOAN_MARKET), this helper looks up the canonical
 * `entityCode` to write to BigQuery so historical and forward rows live
 * under one logical bucket.
 *
 * **Lookup rule.** Find the loan_book under this company+templateId where the
 * anchor appears in `entityCodes[]` OR matches the primary `entityCode`. Case
 * comparison is upper-case-insensitive — anchors arrive uppercased by the
 * extraction regex; loan_book values can be mixed (e.g. legacy 'Peake') so we
 * compare both sides upper-cased.
 *
 * **Ambiguity / pass-through.** When the anchor matches NO loan_book on this
 * tenant+template, returns `{canonicalEntityCode: null, wasRewritten: false}`
 * — the caller should preserve the raw anchor (existing behaviour). This
 * helper never picks a "best guess".
 *
 * **Legacy-mode behaviour.** Returns immediately with `source: 'not_unified'`.
 * Legacy tenants (Entourage) keep their existing routing logic untouched.
 *
 * **Gating.** None. Prefer {@link foldFilenameAnchorToCanonical}, the ungated
 * wrapper every ingestion surface calls; the fold is driven by the loan_book's
 * own `entityCodes[]` declaration (default-empty means no-op), so it needs no
 * per-tenant feature flag. (Historically the admin-ZIP caller gated this on
 * `featureFlags.canonical_entity_code_normalization`; that flag is retired now
 * that the book declaration is the opt-in.) The helper is side-effect-free.
 */
async function resolveCanonicalForFilenameAnchor(db, companyId, templateId, anchor, logger) {
    const log = logger ?? {
        info: (m, ...meta) => console.log(m, ...meta),
        warn: (m, ...meta) => console.warn(m, ...meta),
    };
    const companyDoc = await db.doc(`companies/${companyId}`).get();
    if (!companyDoc.exists) {
        return { canonicalEntityCode: null, wasRewritten: false, source: 'company_missing' };
    }
    const rawMode = companyDoc.data()?.loanBookMode;
    if (rawMode !== 'unified') {
        // Legacy mode: caller keeps the raw anchor; entity_mappings drives routing
        // independently.
        return { canonicalEntityCode: null, wasRewritten: false, source: 'not_unified' };
    }
    const normalizedAnchor = anchor.toUpperCase();
    // `isEnabled` filtered in code (treat missing as enabled) — see the note in
    // resolveEntityCodeForTemplate: a `.where('isEnabled','==',true)` Firestore filter
    // silently drops docs missing the field (the VFA-stage `isActive` incident).
    const snap = await db
        .collection(`companies/${companyId}/loan_books`)
        .where('templateId', '==', templateId)
        .get();
    for (const doc of snap.docs) {
        const data = doc.data();
        if (data.type !== 'entity')
            continue;
        if (data.isEnabled === false)
            continue; // explicitly disabled → exclude; missing field = enabled
        const canonical = data.entityCode;
        if (!canonical || typeof canonical !== 'string')
            continue;
        // Match against the entityCodes[] array first (the collapse declaration).
        if (Array.isArray(data.entityCodes)) {
            for (const c of data.entityCodes) {
                if (typeof c !== 'string')
                    continue;
                if (c.toUpperCase() === normalizedAnchor) {
                    const wasRewritten = canonical.toUpperCase() !== normalizedAnchor;
                    if (wasRewritten) {
                        log.info(`[resolveCanonicalForFilenameAnchor] anchor=${anchor} → canonical=${canonical} ` +
                            `via loan_books/${doc.id}.entityCodes (companyId=${companyId} templateId=${templateId})`);
                    }
                    return {
                        canonicalEntityCode: canonical,
                        wasRewritten,
                        source: 'loan_books_entity_codes',
                    };
                }
            }
        }
        // Match against the primary entityCode (anchor IS the canonical — no rewrite).
        if (canonical.toUpperCase() === normalizedAnchor) {
            return {
                canonicalEntityCode: canonical,
                wasRewritten: false,
                source: 'loan_books_entity_code',
            };
        }
    }
    return { canonicalEntityCode: null, wasRewritten: false, source: 'no_match' };
}
// ──────────────────────────────────────────────────────────────────────────
// foldFilenameAnchorToCanonical
// ──────────────────────────────────────────────────────────────────────────
/**
 * Ungated canonical-anchor fold. Wraps {@link resolveCanonicalForFilenameAnchor}
 * so every unified-mode ingestion surface that stamps `entity_code` from a
 * filename anchor (client upload, admin ZIP, email ingestion) folds an alias
 * anchor to its loan_book's canonical `entityCode` IDENTICALLY.
 *
 * **The loan_book declaration IS the opt-in.** A book that lists `ENTO101` in
 * `entityCodes[]` with `entityCode='ENTM100'` is declaring the alias, so an
 * `ENTO101` upload MUST land as `ENTM100` (else post-cutover BrokerFlow files
 * split the book). This matches the retag migration, which maps every
 * historical `ENTO101` row to `ENTM100`. Books that declare no aliases (and
 * every legacy-mode tenant, where the helper short-circuits `not_unified`) fold
 * to a no-op, so the wrapper is byte-identical for them. That default-empty
 * declaration is why no separate tenant feature flag gates this: unlike the
 * Decru-era `canonical_entity_code_normalization` scaffolding, the fold is
 * unconditional and keyed purely off the book data.
 *
 * Never throws: a lookup failure or a no-match preserves the raw `code`, so an
 * upload is never blocked on the fold.
 */
async function foldFilenameAnchorToCanonical(db, companyId, templateId, code, logger) {
    const log = logger ?? {
        info: (m, ...meta) => console.log(m, ...meta),
        warn: (m, ...meta) => console.warn(m, ...meta),
    };
    try {
        const normalization = await resolveCanonicalForFilenameAnchor(db, companyId, templateId, code, log);
        if (normalization.wasRewritten && normalization.canonicalEntityCode) {
            log.info(`[foldFilenameAnchorToCanonical] folded alias anchor ${code} → canonical ` +
                `${normalization.canonicalEntityCode} (companyId=${companyId} templateId=${templateId} ` +
                `source=${normalization.source})`);
            return normalization.canonicalEntityCode;
        }
    }
    catch (err) {
        log.warn(`[foldFilenameAnchorToCanonical] fold failed for code=${code} companyId=${companyId} ` +
            `templateId=${templateId}: ${err?.message ?? err}. Preserving raw code.`);
    }
    return code;
}
